import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  Tag,
  getProjectTags,
  getPlotcardTags,
  createTag,
  updateTag,
  deleteTag,
  addTagToPlotcard,
  removeTagFromPlotcard,
  getPlotcardsByTag,
  getMultiplePlotcardTags,
  prefetchAndCachePlotcardTags
} from '@/services/supabase/tags';

// Query keys
export const tagKeys = {
  all: ['tags'] as const,
  lists: () => [...tagKeys.all, 'list'] as const,
  list: (filters: string) => [...tagKeys.lists(), { filters }] as const,
  details: () => [...tagKeys.all, 'detail'] as const,
  detail: (id: string) => [...tagKeys.details(), id] as const,
  plotcardTags: (plotcardId: number) => [...tagKeys.all, 'plotcard', plotcardId] as const,
  multiplePlotcardTags: (plotcardIds: number[]) => [...tagKeys.all, 'plotcards', plotcardIds] as const,
  taggedPlotcards: (tagId: number) => [...tagKeys.all, 'plotcards', tagId] as const,
};

// Stale and garbage collection times
const STALE_TIME = 1000 * 60 * 5; // 5 minutes
const GC_TIME = 1000 * 60 * 10; // 10 minutes
// For tag queries, we want more frequent updates
const TAG_STALE_TIME = 1000 * 10; // 10 seconds
const TAG_GC_TIME = 1000 * 60 * 5; // 5 minutes

/**
 * Hook to get all tags for a project
 */
export function useProjectTags(projectId: string | number | undefined) {
  return useQuery({
    queryKey: tagKeys.list(projectId?.toString() || ''),
    queryFn: () => getProjectTags(projectId || ''),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: !!projectId,
  });
}

/**
 * Hook to get tags for a plotcard
 */
export function usePlotcardTags(plotcardId: number | undefined) {
  return useQuery({
    queryKey: tagKeys.plotcardTags(plotcardId || 0),
    queryFn: () => getPlotcardTags(plotcardId || 0),
    staleTime: TAG_STALE_TIME, // Use shorter stale time for more frequent updates
    gcTime: TAG_GC_TIME,
    enabled: !!plotcardId,
    refetchOnWindowFocus: true, // Refetch when window regains focus
    refetchOnMount: true, // Refetch when component mounts
  });
}

/**
 * Hook to get tags for multiple plotcards in a single query
 * This hook includes optimizations to avoid unnecessary API calls
 */
export function useMultiplePlotcardTags(plotcardIds: number[]) {
  const queryClient = useQueryClient();

  // Filter out any duplicate IDs
  const uniqueIds = useMemo(() =>
    Array.from(new Set(plotcardIds)).filter(id => id !== undefined && id !== null),
    [plotcardIds]
  );

  return useQuery({
    queryKey: tagKeys.multiplePlotcardTags(uniqueIds),
    queryFn: () => {
      // Check if we already have cached data for all the plotcard IDs
      const cachedData: Record<number, Tag[]> = {};
      let allCached = true;

      // Check if each individual plotcard has cached data
      for (const id of uniqueIds) {
        const cachedTags = queryClient.getQueryData<Tag[]>(tagKeys.plotcardTags(id));
        if (cachedTags !== undefined) {
          cachedData[id] = cachedTags;
        } else {
          allCached = false;
          break;
        }
      }

      // If we have cached data for all plotcards, return it
      if (allCached && Object.keys(cachedData).length === uniqueIds.length) {
        console.log(`Using cached tag data for ${uniqueIds.length} plotcards`);
        return cachedData;
      }

      // Otherwise, fetch from the API
      console.log(`Fetching tag data for ${uniqueIds.length} plotcards`);
      return getMultiplePlotcardTags(uniqueIds);
    },
    staleTime: TAG_STALE_TIME, // Use shorter stale time for more frequent updates
    gcTime: TAG_GC_TIME,
    enabled: uniqueIds.length > 0,
    refetchOnWindowFocus: true, // Refetch when window regains focus
    refetchOnMount: true, // Refetch when component mounts
  });
}

/**
 * Hook to prefetch and cache tags for all plotcards in a timeline
 * This is meant to be called during the initial page load
 */
export function usePrefetchPlotcardTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (plotcardIds: number[]) =>
      prefetchAndCachePlotcardTags(queryClient, plotcardIds),
  });
}

/**
 * Hook to get plotcards by tag
 */
export function usePlotcardsByTag(tagId: number | undefined) {
  return useQuery({
    queryKey: tagKeys.taggedPlotcards(tagId || 0),
    queryFn: () => getPlotcardsByTag(tagId || 0),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: !!tagId,
  });
}

/**
 * Hook to create a tag
 */
export function useCreateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tag: {
      name: string;
      description?: string;
      color?: string;
      project_id: number;
      category_id?: number | null;
    }) => createTag(tag),
    onSuccess: (newTag, variables) => {
      // Get the current project tags
      const currentTags = queryClient.getQueryData<Tag[]>(
        tagKeys.list(variables.project_id.toString())
      );

      // If we have the current tags in cache, update them optimistically
      if (currentTags) {
        queryClient.setQueryData(
          tagKeys.list(variables.project_id.toString()),
          [...currentTags, newTag]
        );
      }

      // Invalidate the project tags query to force a refetch
      queryClient.invalidateQueries({
        queryKey: tagKeys.list(variables.project_id.toString()),
      });

      // Force an immediate refetch to ensure we have the latest data
      queryClient.refetchQueries({
        queryKey: tagKeys.list(variables.project_id.toString()),
      });
    },
  });
}

/**
 * Hook to update a tag
 */
export function useUpdateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tagId,
      updates,
      projectId,
    }: {
      tagId: number;
      updates: {
        name?: string;
        description?: string | null;
        color?: string;
        category_id?: number | null;
      };
      projectId: string | number;
    }) => updateTag(tagId, updates),
    onSuccess: (_, variables) => {
      // Invalidate the project tags query to force a refetch
      queryClient.invalidateQueries({
        queryKey: tagKeys.list(variables.projectId.toString()),
      });
      // Invalidate the specific tag detail
      queryClient.invalidateQueries({
        queryKey: tagKeys.detail(variables.tagId.toString()),
      });
    },
  });
}

/**
 * Hook to delete a tag
 */
export function useDeleteTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tagId,
      projectId,
    }: {
      tagId: number;
      projectId: string | number;
    }) => deleteTag(tagId),
    onSuccess: (_, variables) => {
      // Invalidate the project tags query to force a refetch
      queryClient.invalidateQueries({
        queryKey: tagKeys.list(variables.projectId.toString()),
      });
    },
  });
}

/**
 * Hook to add a tag to a plotcard
 */
export function useAddTagToPlotcard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      plotcardId,
      tagId,
    }: {
      plotcardId: number;
      tagId: number;
    }) => addTagToPlotcard(plotcardId, tagId),
    // Implement optimistic updates for immediate UI feedback
    onMutate: async (variables) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: tagKeys.plotcardTags(variables.plotcardId) });
      await queryClient.cancelQueries({
        queryKey: tagKeys.multiplePlotcardTags([variables.plotcardId])
      });

      // Get the current tag data
      const previousTags = queryClient.getQueryData<Tag[]>(
        tagKeys.plotcardTags(variables.plotcardId)
      );

      // Get the multiple plotcard tags data
      const previousTagsMap = queryClient.getQueryData<Record<number, Tag[]>>(
        tagKeys.multiplePlotcardTags([variables.plotcardId])
      );

      // Get the tag details from the project tags
      // First try to get the specific project's tags
      let projectId: string | undefined;

      // Try to extract project ID from the plotcard tags query
      const plotcardTagsData = queryClient.getQueryData<any>(
        tagKeys.plotcardTags(variables.plotcardId)
      );

      if (plotcardTagsData && plotcardTagsData[0]?.project_id) {
        projectId = plotcardTagsData[0].project_id.toString();
      }

      // If we have a project ID, try to get its tags
      let allProjectTags: Tag[] = [];
      if (projectId) {
        allProjectTags = queryClient.getQueryData<Tag[]>(
          tagKeys.list(projectId)
        ) || [];
      }

      // If we couldn't get project-specific tags, try to get all tags
      if (allProjectTags.length === 0) {
        // Fall back to all tags
        allProjectTags = queryClient.getQueryData<Tag[]>(
          tagKeys.lists()
        ) || [];
      }

      const tagToAdd = allProjectTags.find(tag => tag.id === variables.tagId);

      if (tagToAdd) {
        // Optimistically update the plotcard tags
        if (previousTags) {
          queryClient.setQueryData(
            tagKeys.plotcardTags(variables.plotcardId),
            [...previousTags, tagToAdd]
          );
        }

        // Also update any multiple plotcard tags queries
        if (previousTagsMap) {
          const updatedTagsMap = { ...previousTagsMap };
          if (!updatedTagsMap[variables.plotcardId]) {
            updatedTagsMap[variables.plotcardId] = [];
          }
          updatedTagsMap[variables.plotcardId] = [
            ...updatedTagsMap[variables.plotcardId],
            tagToAdd
          ];

          queryClient.setQueryData(
            tagKeys.multiplePlotcardTags([variables.plotcardId]),
            updatedTagsMap
          );
        }
      }

      // Return the previous tags for rollback in case of error
      // Initialize previousTagsMap as an empty object if it's undefined
      return {
        previousTags,
        previousTagsMap: previousTagsMap || {}
      };
    },
    onError: (err, variables, context: any) => {
      // If the mutation fails, roll back to the previous value
      if (context?.previousTags) {
        queryClient.setQueryData(
          tagKeys.plotcardTags(variables.plotcardId),
          context.previousTags
        );
      }

      if (context?.previousTagsMap) {
        queryClient.setQueryData(
          tagKeys.multiplePlotcardTags([variables.plotcardId]),
          context.previousTagsMap
        );
      }
    },
    onSuccess: async (_, variables) => {
      // Get the latest data directly from the API
      const updatedTags = await getPlotcardTags(variables.plotcardId);

      // Update the cache directly with the latest data
      queryClient.setQueryData(
        tagKeys.plotcardTags(variables.plotcardId),
        updatedTags
      );

      // Update the multiple plotcard tags cache
      const currentTagsMap = queryClient.getQueryData<Record<number, Tag[]>>(
        tagKeys.multiplePlotcardTags([variables.plotcardId])
      ) || {};

      const updatedTagsMap = {
        ...currentTagsMap,
        [variables.plotcardId]: updatedTags
      };

      queryClient.setQueryData(
        tagKeys.multiplePlotcardTags([variables.plotcardId]),
        updatedTagsMap
      );

      // Also update any other multiple plotcard tags queries that might include this plotcard
      queryClient.invalidateQueries({
        queryKey: tagKeys.all,
        predicate: (query) => {
          // Only invalidate queries that start with the multiplePlotcardTags prefix
          const queryKey = query.queryKey as any[];
          return queryKey.length >= 3 &&
                 queryKey[0] === 'tags' &&
                 queryKey[1] === 'plotcards' &&
                 Array.isArray(queryKey[2]) &&
                 // Don't invalidate the one we just updated
                 !queryKey[2].every(id => id === variables.plotcardId);
        },
        // Don't refetch immediately to avoid UI flicker
        refetchType: 'none'
      });

      // Invalidate the tagged plotcards query
      queryClient.invalidateQueries({
        queryKey: tagKeys.taggedPlotcards(variables.tagId),
        // Don't refetch immediately to avoid UI flicker
        refetchType: 'none'
      });
    },
  });
}

/**
 * Hook to remove a tag from a plotcard
 */
export function useRemoveTagFromPlotcard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      plotcardId,
      tagId,
    }: {
      plotcardId: number;
      tagId: number;
    }) => removeTagFromPlotcard(plotcardId, tagId),
    // Implement optimistic updates for immediate UI feedback
    onMutate: async (variables) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: tagKeys.plotcardTags(variables.plotcardId) });
      await queryClient.cancelQueries({
        queryKey: tagKeys.multiplePlotcardTags([variables.plotcardId])
      });

      // Get the current tag data
      const previousTags = queryClient.getQueryData<Tag[]>(
        tagKeys.plotcardTags(variables.plotcardId)
      );

      // Get the multiple plotcard tags data
      const previousTagsMap = queryClient.getQueryData<Record<number, Tag[]>>(
        tagKeys.multiplePlotcardTags([variables.plotcardId])
      );

      // Optimistically update the plotcard tags
      if (previousTags) {
        const updatedTags = previousTags.filter(tag => tag.id !== variables.tagId);
        queryClient.setQueryData(
          tagKeys.plotcardTags(variables.plotcardId),
          updatedTags
        );
      }

      if (previousTagsMap && previousTagsMap[variables.plotcardId]) {
        const updatedTagsMap = { ...previousTagsMap };
        updatedTagsMap[variables.plotcardId] = updatedTagsMap[variables.plotcardId].filter(
          tag => tag.id !== variables.tagId
        );

        queryClient.setQueryData(
          tagKeys.multiplePlotcardTags([variables.plotcardId]),
          updatedTagsMap
        );
      }

      // Return the previous tags for rollback in case of error
      // Initialize previousTagsMap as an empty object if it's undefined
      return {
        previousTags,
        previousTagsMap: previousTagsMap || {}
      };
    },
    onError: (err, variables, context: any) => {
      // If the mutation fails, roll back to the previous value
      if (context?.previousTags) {
        queryClient.setQueryData(
          tagKeys.plotcardTags(variables.plotcardId),
          context.previousTags
        );
      }

      if (context?.previousTagsMap) {
        queryClient.setQueryData(
          tagKeys.multiplePlotcardTags([variables.plotcardId]),
          context.previousTagsMap
        );
      }
    },
    onSuccess: async (_, variables) => {
      // Get the latest data directly from the API
      const updatedTags = await getPlotcardTags(variables.plotcardId);

      // Update the cache directly with the latest data
      queryClient.setQueryData(
        tagKeys.plotcardTags(variables.plotcardId),
        updatedTags
      );

      // Update the multiple plotcard tags cache
      const currentTagsMap = queryClient.getQueryData<Record<number, Tag[]>>(
        tagKeys.multiplePlotcardTags([variables.plotcardId])
      ) || {};

      const updatedTagsMap = {
        ...currentTagsMap,
        [variables.plotcardId]: updatedTags
      };

      queryClient.setQueryData(
        tagKeys.multiplePlotcardTags([variables.plotcardId]),
        updatedTagsMap
      );

      // Also update any other multiple plotcard tags queries that might include this plotcard
      queryClient.invalidateQueries({
        queryKey: tagKeys.all,
        predicate: (query) => {
          // Only invalidate queries that start with the multiplePlotcardTags prefix
          const queryKey = query.queryKey as any[];
          return queryKey.length >= 3 &&
                 queryKey[0] === 'tags' &&
                 queryKey[1] === 'plotcards' &&
                 Array.isArray(queryKey[2]) &&
                 // Don't invalidate the one we just updated
                 !queryKey[2].every(id => id === variables.plotcardId);
        },
        // Don't refetch immediately to avoid UI flicker
        refetchType: 'none'
      });

      // Invalidate the tagged plotcards query
      queryClient.invalidateQueries({
        queryKey: tagKeys.taggedPlotcards(variables.tagId),
        // Don't refetch immediately to avoid UI flicker
        refetchType: 'none'
      });
    },
  });
}
